<template>
  <div class="item-summary-bar">
    <!-- Header -->
    <div class="summary-header">
      
      <!-- Overall Progress -->
      <div class="overall-progress">
        <div class="progress-label">
          <TrendingUpIcon class="summary-icon" />
          <span>{{ total_ready_items }}/{{ total_active_items }}</span>
        </div>
        <div class="progress-bar-container">
          <div 
            :class="['progress-bar-fill', { 'complete': total_ready_items === total_active_items && total_active_items > 0 }]"
            :style="{ width: total_active_items > 0 ? ((total_ready_items / total_active_items) * 100) + '%' : '0%' }"
          />
        </div>
      </div>
    </div>

    <!-- Scrollable Items List -->
    <div class="items-list">
      <div class="items-container">
        <div 
          v-for="[itemName, counts] in items" 
          :key="itemName"
          :class="['item-card', { 'priority': true }]"
        >
          <div class="item-header">
            <h3 class="item-name">{{ itemName }}</h3>
          </div>
          <div class="item-status-row">
            <div class="item-badges">
              <div class="badge badge-ready">
                ✓ {{ counts.ready }}
              </div>
              <div class="badge badge-pending">
                ⏳ {{ counts.pending }}
              </div>
            </div>
            <div class="item-progress">
              <div class="item-progress-bar">
                <div 
                  :class="['item-progress-fill', { 'complete': counts.ready === counts.total }]"
                  :style="{ width: ((counts.ready / counts.total) * 100) + '%' }"
                />
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TrendingUpIcon from './icons/TrendingUpIcon.vue'

export default {
  name: 'ItemSummaryBar',
  components: {
    TrendingUpIcon
  },
  props: {
    orders: {
      type: Array,
      required: true
    },
    priority_items: {
      type: Array,
      required: false
    }
  },
  computed: {
    itemsDict() {
      return this.orders.reduce((acc, order) => {
        order.items.forEach(item => {
          let idx = 0
          if (this.priority_items) {
            idx = this.priority_items.indexOf(item.item_name)
            if (idx === -1) {
              return // Skip non-priority items
            }
          }
          if (!acc[item.item_name]) {
            acc[item.item_name] = { total: 0, ready: 0, pending: 0, idx: idx }
          }
          acc[item.item_name].total += item.qty
          if (item.status == 'Ready') {
            acc[item.item_name].ready += item.qty
          } else {
            acc[item.item_name].pending += item.qty
          }
        })
        return acc
      }, {})
    },
    items() {
      return Object.entries(this.itemsDict)
        .sort(([nameA, dictA], [nameB, dictB]) => {
          if (dictA.idx !== dictB.idx) {
            return dictA.idx - dictB.idx // Sort by priority index
          }
          // If same priority, sort by name
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
        })
        .slice(0, 20) // Show top 20 items only
    },
    total_active_items() {
      return Object.values(this.itemsDict).reduce((sum, counts) => sum + counts.total, 0)
    },
    total_ready_items() {
      return Object.values(this.itemsDict).reduce((sum, counts) => sum + counts.ready, 0)
    }
  }
}
</script>

<style scoped>
.item-summary-bar {
  width: 270px;
  background: rgba(15, 23, 42, 0.9);
  border-right: 1px solid #374151;
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  height: calc(100vh - 70px);
}

.summary-header {
  padding: 0.5rem;
  border-bottom: 1px solid #374151;
  flex-shrink: 0;
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.summary-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #60a5fa;
}

.summary-title h2 {
  font-weight: 600;
  color: white;
  font-size: 1rem;
  margin: 0;
}

.overall-progress {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}

.progress-label {
  display: flex;
  font-size: 0.85rem;
  gap: 0.5em;
  color: #94a3b8;
}

.progress-bar-container {
  width: 100%;
  height: 0.5rem;
  background: #1e293b;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 9999px;
  transition: all 0.5s;
  background: #3b82f6;
}

.progress-bar-fill.complete {
  background: #10b981;
}

.items-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem;
  min-height: 0;
}

.items-container {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding-bottom: 1rem;
}

.item-card {
  padding: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid #374151;
  background: rgba(30, 41, 59, 0.5);
  transition: all 0.3s;
}

.item-card.priority {
  background: rgba(59, 130, 246, 0.3);
  border-color: rgba(59, 130, 246, 0.5);
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.2);
}

.item-header {
  margin-bottom: 0.5rem;
}

.item-name {
  font-weight: 500;
  color: white;
  font-size: 16px;
  line-height: 1.25;
  margin: 0;
}

.item-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.item-badges {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.badge {
  font-size: 15px;
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid;
  font-weight: 500;
}

.badge-ready {
  background: rgba(16, 185, 129, 0.2);
  color: #6ee7b7;
  border-color: rgba(16, 185, 129, 0.3);
}

.badge-pending {
  background: rgba(245, 158, 11, 0.2);
  color: #fcd34d;
  border-color: rgba(245, 158, 11, 0.3);
}

.item-progress {
  flex: 1;
  min-width: 0;
}

.item-progress-bar {
  width: 100%;
  height: 0.375rem;
  background: #1e293b;
  border-radius: 9999px;
  overflow: hidden;
}

.item-progress-fill {
  height: 100%;
  border-radius: 9999px;
  transition: all 0.5s;
  background: #3b82f6;
}

.item-progress-fill.complete {
  background: #10b981;
}

/* Scrollbar Styling */
.items-list::-webkit-scrollbar {
  width: 6px;
}

.items-list::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.5);
  border-radius: 3px;
}

.items-list::-webkit-scrollbar-thumb {
  background: rgba(55, 65, 81, 0.8);
  border-radius: 3px;
}

.items-list::-webkit-scrollbar-thumb:hover {
  background: rgba(75, 85, 99, 0.9);
}
</style>